import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import { T } from '@sm/intl';
import { createUseStyles } from 'react-jss';
import { Box } from '@wds/box';
import { Checkbox } from '@wds/checkbox';
import LinkStyleButton from '../../shared/LinkStyleButton';
import { List } from '@wds/list';
import { Typography } from '@wds/typography';
import ThemeRow from './ThemeRow';
import { getResponseCountForTheme, mergeSelectedIdsAndAutoGeneratedIds } from '../../lib';
import OpenEndedCodingContext from '../../OpenEndedCodingContext';
import { useFilters, useFiltersActions, useThemes } from '../../Context';
import { ALL_FILTER_ID, ALL_FILTER_ID_BLANK, INDETERMINATE_FILTER_ID } from '../../constants';
export const COPY = {
  emptyStateRepeatedly: {
    id: 'OpenEndedCoding.ThemeSelector.emptyStateRepeatedly',
    defaultMessage: 'As you read your responses, you’ll probably notice some topics, or <strong>themes</strong>, come up repeatedly.',
    description: '[Type: Header][Vis: med] - empty state theme selection'
  },
  emptyStateThematic: {
    id: 'OpenEndedCoding.ThemeSelector.emptyStateThematic',
    defaultMessage: 'These themes can reveal important concepts and patterns in your data. <strong>Thematic analysis</strong> is the process of identifying what these themes are and where they appear.',
    description: '[Type: Header][Vis: med] - empty state theme selection'
  },
  selectAll: {
    id: 'OpenEndedCoding.ThemeSelector.selectAll',
    defaultMessage: 'Select all',
    description: '[Type: Header][Vis: med] - option to select all themes'
  },
  clearFilter: {
    id: 'OpenEndedCoding:ThemeSelector.clearFilter',
    defaultMessage: 'Clear filter',
    description: '[Type: Header][Vis: med] - option to clear all filters'
  }
};
const useStyles = createUseStyles(() => ({
  list: {
    // remove the default list item padding to maximize width
    // as the theme selector section has a reduced grid portion.
    '& div[id^="theme-row"] > span[class^="wrenchListLabel"]': {
      padding: 0,
      margin: 0
    }
  },
  hide: {
    visibility: 'hidden'
  }
}));
const ThemeSection = ({
  onClickDelete,
  onClickEdit
}) => {
  const classes = useStyles();
  const {
    themes
  } = useThemes();
  const {
    selectedThemes,
    filterByTaggedStatus,
    sortValue,
    searchInput
  } = useFilters();
  const {
    filterResponses
  } = useContext(OpenEndedCodingContext);
  const {
    setSelectedThemes
  } = useFiltersActions();
  const [userAction, setUserAction] = useState(false);
  const selectAllChecked = selectedThemes.includes(ALL_FILTER_ID) && !selectedThemes.includes(ALL_FILTER_ID_BLANK) || selectedThemes.includes(INDETERMINATE_FILTER_ID);
  // if a new theme has been added and select all is checked then make sure the new theme is checked
  useEffect(() => {
    if (selectedThemes.includes(ALL_FILTER_ID) && !selectedThemes.includes(ALL_FILTER_ID_BLANK)) {
      setSelectedThemes([ALL_FILTER_ID, ...themes.map(theme => theme.id)]);
    }
  }, [themes]);
  // theme filter
  const handleThemeSelect = id => {
    setUserAction(true);
    let selectedThemesTemp = selectedThemes;
    // we have the BLANK marker, so we need to remove all themes
    // the selected theme will get added back in
    if (selectedThemesTemp.includes(ALL_FILTER_ID_BLANK)) {
      selectedThemesTemp = [];
    }
    if (id === ALL_FILTER_ID) {
      // we have toggled Select all
      selectedThemesTemp = selectedThemesTemp.filter(tid => tid !== INDETERMINATE_FILTER_ID);
      if (selectedThemesTemp.includes(ALL_FILTER_ID)) {
        // uncheck all - return to blank state
        selectedThemesTemp = [];
      } else {
        // add all themes
        selectedThemesTemp = [ALL_FILTER_ID, ...themes.map(theme => theme.id)];
      }
    } else {
      // normal theme
      if (selectedThemesTemp.includes(ALL_FILTER_ID)) {
        // select all is checked, so remove ALL_FILTER_ID and add INDETERMINATE_FILTER_ID
        selectedThemesTemp = [...selectedThemesTemp.filter(tid => tid !== ALL_FILTER_ID), INDETERMINATE_FILTER_ID];
      }
      // this acts as a toggle
      // remove the theme if it already exists, include it if it doesn't exist
      selectedThemesTemp = selectedThemesTemp.includes(id) ? selectedThemesTemp.filter(tid => tid !== id) : [...selectedThemesTemp, id];
      // if we have all the themes again, add ALL_FILTER_ID back in and set Indeterminate to false
      // plus one for the INDETERMINATE_FILTER
      if (selectedThemesTemp.length === themes.length + 1) {
        selectedThemesTemp = [...selectedThemesTemp.filter(tid => tid !== INDETERMINATE_FILTER_ID), ALL_FILTER_ID];
      }
    }
    // if we are at the blank state, add all themes back in and the BLANK marker
    if (selectedThemesTemp.length === 0) {
      selectedThemesTemp = [ALL_FILTER_ID, ALL_FILTER_ID_BLANK, ...themes.map(theme => theme.id)];
    }
    // update state
    setSelectedThemes(selectedThemesTemp);
  };
  const handleClearFilter = () => {
    // back to blank state
    setSelectedThemes([ALL_FILTER_ID, ALL_FILTER_ID_BLANK, ...themes.map(theme => theme.id)]);
    setUserAction(true);
  };
  // execute the filter on user actions
  useEffect(() => {
    if (userAction) {
      filterResponses({
        themeIds: mergeSelectedIdsAndAutoGeneratedIds(themes, selectedThemes),
        filterByTaggedStatus: filterByTaggedStatus,
        sortBy: sortValue,
        search: searchInput
      });
      setUserAction(false);
    }
  }, [selectedThemes, userAction]);
  // Empty state
  if (themes.length === 0) {
    return _jsxs(Box, {
      mb: 4,
      "data-testid": "ThemeSection__EmptyState",
      children: [_jsx(Box, {
        mb: 2,
        children: _jsx(Typography, {
          component: "p",
          variant: "bodySm",
          children: _jsx(T, {
            desc: COPY.emptyStateRepeatedly,
            html: true
          })
        })
      }), _jsx(Box, {
        children: _jsx(Typography, {
          component: "p",
          variant: "bodySm",
          children: _jsx(T, {
            desc: COPY.emptyStateThematic,
            html: true
          })
        })
      })]
    });
  }
  return _jsx(Box, {
    className: classes.list,
    children: _jsxs(List, {
      dense: true,
      noRules: true,
      children: [_jsx(Box, {
        mt: 3,
        mb: 3,
        children: _jsx(List.Item, {
          id: `theme-row-${ALL_FILTER_ID}`,
          children: _jsxs(Box, {
            display: "flex",
            justifyContent: "space-between",
            children: [_jsxs(Box, {
              display: "flex",
              children: [_jsx(Checkbox, {
                "data-testid": `Checkbox-${ALL_FILTER_ID}`,
                id: `checkbox-${ALL_FILTER_ID}`,
                value: ALL_FILTER_ID,
                onChange: () => handleThemeSelect(ALL_FILTER_ID),
                indeterminate: selectedThemes.includes(INDETERMINATE_FILTER_ID),
                checked: selectAllChecked
              }), _jsx(Box, {
                ml: 2,
                children: _jsx(Typography, {
                  component: "div",
                  variant: "bodySm",
                  color: "dark",
                  children: _jsx(T, {
                    desc: COPY.selectAll
                  })
                })
              })]
            }), _jsx(Box, {
              children: selectedThemes.indexOf(ALL_FILTER_ID_BLANK) == -1 && _jsx(LinkStyleButton, {
                onClick: handleClearFilter,
                children: _jsx(T, {
                  desc: COPY.clearFilter
                })
              })
            })]
          })
        }, ALL_FILTER_ID)
      }), themes.map(theme => _jsx(List.Item, {
        id: `theme-row-${theme.id}`,
        children: _jsx(ThemeRow, {
          theme: theme.theme,
          id: theme.id,
          count: getResponseCountForTheme(theme),
          onClickDelete: () => onClickDelete(theme.id),
          onClickEdit: () => onClickEdit(theme.id),
          handleThemeSelect: () => handleThemeSelect(theme.id)
        })
      }, theme.id))]
    })
  });
};
export default ThemeSection;